import { useEffect, useMemo, useState } from "react";
import { Backdrop, Button, Checkbox, CircularProgress, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as ExcelJS from "exceljs";
import dayjs from "dayjs";
import { saveAs } from "file-saver";

import { findAcoesRelatorioGeral } from "API/apiRelatoriosTurma";
import {
  findRelatorioConsultaParticipantes,
  findRelatorioConsultaParticipantesCount,
  RelatorioConsultaParticipantesParams,
} from "API/apiRelatoriosTurma";
import CpfField from "components/atoms/CpfField/CpfField";
import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { MultiSelectOption } from "components/atoms/MultiSelectField/MultiSelectOptions";
import { useAppFeedback } from "hooks/useAppFeedback";
import { useLocalidadeStore } from "store/useLocalidadeStore";
import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import { convertListSelect } from "utils/Arrays";
import { formatarCpf } from "utils/Masks";
import { getReportPeriodError } from "utils/reportPeriod";
import { addGovernmentReportLogo, prependGovernmentReportHeader } from "utils/reportExportHeader";

import styles from "../Relatorios.module.css";

type RelatorioConsultaParticipantesProps = {
  selectedReportLabel: string;
};

type SearchMode = "cpf" | "municipio";
type SelectValue = Array<number | string>;

type AcaoOption = {
  id: number;
  nome: string;
};

type PresencaItem = {
  data_presenca?: string | null;
  tipo?: string | null;
};

type RelatorioConsultaItem = {
  municipio_nome?: string | null;
  ambito_nome?: string | null;
  orgao_atuacao_ou_lotacao?: string | null;
  usuario?: {
    nome?: string | null;
    cpf?: string | null;
    possui_nome_social?: boolean | null;
    nome_social?: string | null;
  };
  acao?: {
    nome?: string | null;
    data_inicio?: string | null;
    data_fim?: string | null;
  };
  turma?: {
    nome?: string | null;
    data_inicio?: string | null;
    data_fim?: string | null;
  };
  presencas?: PresencaItem[];
  nota?: number | null;
  status?: string | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};

const formatDateRange = (start?: string | null, end?: string | null) => {
  const startFormatted = formatDate(start);
  const endFormatted = formatDate(end);
  if (!startFormatted && !endFormatted) return "";
  return `${startFormatted} - ${endFormatted}`;
};

const getPresencasTexto = (presencas?: PresencaItem[]) =>
  (presencas || [])
    .filter((item) => item.tipo === "PRESENTE" && item.data_presenca)
    .map((item) => formatDate(item.data_presenca))
    .filter(Boolean)
    .join(", ");

const buildHeader = (worksheet: ExcelJS.Worksheet, columnsLength: number, headerRowNumber = 1) => {
  const headerRow = worksheet.getRow(headerRowNumber);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF004D40" },
    };
    cell.font = {
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
  });

  worksheet.autoFilter = {
    from: { row: headerRowNumber, column: 1 },
    to: { row: headerRowNumber, column: columnsLength },
  };
};

const RelatorioConsultaParticipantes = ({
  selectedReportLabel,
}: RelatorioConsultaParticipantesProps) => {
  const { showError, showSuccess } = useAppFeedback();
  const ensureEstados = useLocalidadeStore((state) => state.ensureEstados);
  const ensureCidades = useLocalidadeStore((state) => state.ensureCidades);
  const ensureRegionais = useLocalidadeStore((state) => state.ensureRegionais);
  const regionais = useLocalidadeStore((state) => state.regionais);

  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const periodError = getReportPeriodError(dataInicial, dataFinal);
  const periodValidationError = dataInicial && dataFinal ? periodError : null;
  const [searchMode, setSearchMode] = useState<SearchMode>("cpf");
  const [cpf, setCpf] = useState("");
  const [todosMunicipiosEstado, setTodosMunicipiosEstado] = useState(false);
  const [acoesSelecionadas, setAcoesSelecionadas] = useState<SelectValue>([]);
  const [municipiosSelecionados, setMunicipiosSelecionados] = useState<SelectValue>([]);
  const [regionaisSelecionadas, setRegionaisSelecionadas] = useState<SelectValue>([]);

  const [acoesOptions, setAcoesOptions] = useState<MultiSelectOption[]>([]);
  const [municipiosOptions, setMunicipiosOptions] = useState<MultiSelectOption[]>([]);
  const [regionaisOptions, setRegionaisOptions] = useState<MultiSelectOption[]>([]);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingAcoes, setLoadingAcoes] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

  const selectedAcaoIds = useMemo(
    () => acoesSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [acoesSelecionadas],
  );
  const selectedMunicipioIds = useMemo(
    () => municipiosSelecionados.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [municipiosSelecionados],
  );
  const selectedRegionaisIds = useMemo(
    () => regionaisSelecionadas.map((item) => Number(item)).filter((item) => !Number.isNaN(item)),
    [regionaisSelecionadas],
  );
  const cpfSanitizado = useMemo(() => cpf.replace(/\D/g, ""), [cpf]);

  const hasRequiredFiltersForCount = useMemo(() => {
    if (periodError || selectedAcaoIds.length === 0) {
      return false;
    }

    if (searchMode === "cpf") {
      return cpfSanitizado.length === 11;
    }

    return todosMunicipiosEstado || selectedMunicipioIds.length > 0 || selectedRegionaisIds.length > 0;
  }, [
    cpfSanitizado.length,
    dataFinal,
    dataInicial,
    periodError,
    searchMode,
    selectedAcaoIds.length,
    selectedMunicipioIds.length,
    selectedRegionaisIds.length,
    todosMunicipiosEstado,
  ]);

  useEffect(() => {
    const loadBaseOptions = async () => {
      try {
        setLoadingOptions(true);
        const estados = await ensureEstados();
        const estadoMG = estados.find((item) => item.sigla === "MG");
        if (!estadoMG) {
          throw new Error("Estado MG nao encontrado.");
        }

        const [municipiosMg] = await Promise.all([
          ensureCidades({ estado_id: estadoMG.estado_id }),
          ensureRegionais(),
        ]);

        setMunicipiosOptions(
          convertListSelect(
            [...municipiosMg].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
            "id",
            "nome",
          ),
        );
      } catch (error) {
        showError("Nao foi possivel carregar os filtros do relatorio.", error);
      } finally {
        setLoadingOptions(false);
      }
    };

    void loadBaseOptions();
  }, [ensureCidades, ensureEstados, ensureRegionais, showError]);

  useEffect(() => {
    setRegionaisOptions(
      convertListSelect(
        [...regionais].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
        "id",
        "nome",
      ),
    );
  }, [regionais]);

  useEffect(() => {
    if (periodError) {
      setAcoesOptions([]);
      setAcoesSelecionadas([]);
      setEstimatedCount(null);
      return;
    }

    const loadAcoes = async () => {
      try {
        setLoadingAcoes(true);
        const response = await findAcoesRelatorioGeral(dataInicial, dataFinal);
        const acoes = response.acoes || [];
        setAcoesOptions(acoes.map((item: AcaoOption) => ({ id: item.id, nome: item.nome })));
        setAcoesSelecionadas([]);
        setEstimatedCount(null);
      } catch (error) {
        setAcoesOptions([]);
        setAcoesSelecionadas([]);
        setEstimatedCount(null);
        showError("Nao foi possivel carregar as ações do relatório.", error);
      } finally {
        setLoadingAcoes(false);
      }
    };

    void loadAcoes();
  }, [dataFinal, dataInicial, periodError, showError]);

  useEffect(() => {
    if (searchMode === "cpf") {
      setTodosMunicipiosEstado(false);
      setMunicipiosSelecionados([]);
      setRegionaisSelecionadas([]);
    } else {
      setCpf("");
    }
  }, [searchMode]);

  const requestParams = useMemo<RelatorioConsultaParticipantesParams | null>(() => {
    if (periodError || selectedAcaoIds.length === 0) {
      return null;
    }

    if (searchMode === "cpf") {
      if (cpfSanitizado.length !== 11) {
        return null;
      }

      return {
        data_inicio: dataInicial,
        data_fim: dataFinal,
        acoes_ids: selectedAcaoIds,
        cpf: cpfSanitizado,
      };
    }

    if (!todosMunicipiosEstado && selectedMunicipioIds.length === 0 && selectedRegionaisIds.length === 0) {
      return null;
    }

    return {
      data_inicio: dataInicial,
      data_fim: dataFinal,
      acoes_ids: selectedAcaoIds,
      todos_municipios_estado: todosMunicipiosEstado || undefined,
      municipios_ids: !todosMunicipiosEstado && selectedMunicipioIds.length > 0 ? selectedMunicipioIds : undefined,
      regionais_ids: !todosMunicipiosEstado && selectedRegionaisIds.length > 0 ? selectedRegionaisIds : undefined,
    };
  }, [
    cpfSanitizado,
    dataFinal,
    dataInicial,
    periodError,
    searchMode,
    selectedAcaoIds,
    selectedMunicipioIds,
    selectedRegionaisIds,
    todosMunicipiosEstado,
  ]);

  useEffect(() => {
    if (!hasRequiredFiltersForCount) {
      setEstimatedCount(null);
      return;
    }

    if (!requestParams) {
      setEstimatedCount(null);
      return;
    }

    const loadCount = async () => {
      try {
        setLoadingCount(true);
        const response = await findRelatorioConsultaParticipantesCount(requestParams);
        setEstimatedCount(response.count ?? 0);
      } catch (error) {
        setEstimatedCount(null);
        showError("Nao foi possivel calcular o resultado estimado do relatório.", error);
      } finally {
        setLoadingCount(false);
      }
    };

    void loadCount();
  }, [hasRequiredFiltersForCount, requestParams, showError]);

  const handleClear = () => {
    setDataInicial("");
    setDataFinal("");
    setSearchMode("cpf");
    setCpf("");
    setTodosMunicipiosEstado(false);
    setAcoesSelecionadas([]);
    setMunicipiosSelecionados([]);
    setRegionaisSelecionadas([]);
    setAcoesOptions([]);
    setEstimatedCount(null);
  };

  const buildWorkbook = (rows: RelatorioConsultaItem[]) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Consulta Participantes", {
      views: [{ state: "frozen", xSplit: 1, ySplit: 5 }],
    });

    const headers = [
      "MUNICÍPIO",
      "ÂMBITO",
      "ÓRGÃO DE ATUAÇÃO / LOTAÇÃO",
      "NOME COMPLETO",
      "CPF",
      "AÇÃO",
      "TURMA",
      "DATA DA AÇÃO (INÍCIO E FIM)",
      "DATA DA TURMA (INÍCIO E FIM)",
      "DATA(S) DE PRESENÇA",
      "NOTA",
      "STATUS",
    ];

    worksheet.columns = headers.map((header, index) => ({
      header,
      key: `col_${index}`,
      width: index === 0 ? 24 : Math.max(18, Math.min(36, header.length + 4)),
    }));

    const headerRowNumber = prependGovernmentReportHeader(worksheet, headers.length) + 1;
    buildHeader(worksheet, headers.length, headerRowNumber);

    rows.forEach((item) => {
      const row = worksheet.addRow([
        item.municipio_nome || "",
        item.ambito_nome || "",
        item.orgao_atuacao_ou_lotacao || "",
        item.usuario?.possui_nome_social ? item.usuario?.nome_social || "" : item.usuario?.nome || "",
        formatarCpf(item.usuario?.cpf || ""),
        item.acao?.nome || "",
        item.turma?.nome || "",
        formatDateRange(item.acao?.data_inicio, item.acao?.data_fim),
        formatDateRange(item.turma?.data_inicio, item.turma?.data_fim),
        getPresencasTexto(item.presencas),
        item.nota ?? "",
        item.status || "",
      ]);

      const status = item.status;
      if (status === "APROVADO") {
        row.getCell(headers.length).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFDCFCE7" },
        };
      }
      if (status === "REPROVADO") {
        row.getCell(headers.length).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFDE2E2" },
        };
      }
    });

    return workbook;
  };

  const handleGenerateReport = async () => {
    if (periodError) {
      showError(periodError);
      return;
    }

    if (!requestParams) {
      showError("Preencha os filtros obrigatórios para gerar o relatório.");
      return;
    }

    try {
      setGeneratingReport(true);
      const response = await findRelatorioConsultaParticipantes(requestParams);
      const workbook = buildWorkbook((response.data || []) as RelatorioConsultaItem[]);
      await Promise.all(workbook.worksheets.map((worksheet) => addGovernmentReportLogo(workbook, worksheet)));
      const excelBuffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        "relatorio_consulta_participantes.xlsx",
      );

      showSuccess("Relatório gerado com sucesso.");
    } catch (error) {
      showError("Nao foi possivel gerar o relatório.", error);
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <>
      <div className={styles.reportFilterBody}>
        <div className={styles.reportHeaderRow}>
          <div className={styles.reportSelectedWrap}>
            <span className={styles.reportSelectedLabel}>Relatório selecionado:</span>
            <span className={styles.reportSelectedChip}>{selectedReportLabel}</span>
          </div>

          <div className={styles.exportFormatCard}>
            <div className={styles.exportFormatLabel}>Formato de exportação</div>
            <div className={styles.exportFormatValue}>
              <TableChartOutlinedIcon className={styles.exportFormatIcon} />
              XLSX
            </div>
          </div>
        </div>

        <div className={styles.reportDivider}></div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Período <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <div className={styles.filterGridTwo}>
            <div>
              <label className={stylesInput.titleInputSoft}>Data inicial</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataInicial}
                error={Boolean(periodValidationError)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataInicial(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
            <div>
              <label className={stylesInput.titleInputSoft}>Data final</label>
              <TextField
                type="date"
                fullWidth
                size="small"
                variant="standard"
                value={dataFinal}
                error={Boolean(periodValidationError)}
                helperText={periodValidationError || undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDataFinal(event.target.value)}
                InputProps={{ className: stylesInput.inputSoft, disableUnderline: true }}
              />
            </div>
          </div>
        </div>

        <div className={styles.filterSection}>
          <div className={styles.filterBlockTitle}>
            Selecione a(s) ação(ões) <span className={styles.optionalText}>(obrigatório)</span>
          </div>
          <MultiSelectCheck
            label=""
            placeholder={
              dataInicial && dataFinal ? "Selecione uma ou mais ações" : "Preencha primeiro a data inicial e a data final"
            }
            fullWidth
            value={acoesSelecionadas}
            onChange={setAcoesSelecionadas}
            options={acoesOptions}
            disabled={!dataInicial || !dataFinal || loadingAcoes}
            selectedDisplayMode="count"
            paperClassname={stylesInput.paper}
            popperClassname={stylesInput.popper}
            titleInputClassname={styles.reportHiddenLabel}
            inputSelectClassname={stylesInput.inputSoft}
            labelSelectClassname={stylesInput.labelSelect}
          />
        </div>

        <div className={styles.filterSection}>
          <div className={`${styles.filterBlockTitle} ${styles.reportSearchModeTitle}`}>Modo de busca</div>
          <RadioGroup
            row
            value={searchMode}
            onChange={(event) => setSearchMode(event.target.value as SearchMode)}
            className={`${styles.reportCheckboxGroup} ${styles.reportSearchModeGroup}`}
          >
            <FormControlLabel value="cpf" control={<Radio />} label="Buscar por CPF" />
            <FormControlLabel value="municipio" control={<Radio />} label="Buscar por Município" />
          </RadioGroup>
        </div>

        {searchMode === "cpf" && (
          <div className={styles.filterSection}>
            <div className={styles.filterBlockTitle}>CPF</div>
            <CpfField
              value={cpf}
              onUpdate={setCpf}
              customClassName={stylesInput.inputSoft}
              label=""
              placeholder="Digite o CPF"
            />
          </div>
        )}

        {searchMode === "municipio" && (
          <>
            <div className={styles.filterSection}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={todosMunicipiosEstado}
                    onChange={(event) => {
                      setTodosMunicipiosEstado(event.target.checked);
                      if (event.target.checked) {
                        setMunicipiosSelecionados([]);
                        setRegionaisSelecionadas([]);
                      }
                    }}
                  />
                }
                label="Todos municipios do estado"
              />
            </div>

            <div className={styles.filterGridTwo}>
              <div className={styles.filterSection}>
                <div className={styles.filterBlockTitle}>Municípios de MG</div>
                <MultiSelectCheck
                  label=""
                  placeholder="Selecione um ou mais municípios"
                  fullWidth
                  value={municipiosSelecionados}
                  onChange={setMunicipiosSelecionados}
                  options={municipiosOptions}
                  disabled={todosMunicipiosEstado || loadingOptions}
                  selectedDisplayMode="count"
                  paperClassname={stylesInput.paper}
                  popperClassname={stylesInput.popper}
                  titleInputClassname={styles.reportHiddenLabel}
                  inputSelectClassname={stylesInput.inputSoft}
                  labelSelectClassname={stylesInput.labelSelect}
                />
              </div>

              <div className={styles.filterSection}>
                <div className={styles.filterBlockTitle}>Regionais</div>
                <MultiSelectCheck
                  label=""
                  placeholder="Selecione uma ou mais regionais"
                  fullWidth
                  value={regionaisSelecionadas}
                  onChange={setRegionaisSelecionadas}
                  options={regionaisOptions}
                  disabled={todosMunicipiosEstado || loadingOptions}
                  selectedDisplayMode="count"
                  paperClassname={stylesInput.paper}
                  popperClassname={stylesInput.popper}
                  titleInputClassname={styles.reportHiddenLabel}
                  inputSelectClassname={stylesInput.inputSoft}
                  labelSelectClassname={stylesInput.labelSelect}
                />
              </div>
            </div>
          </>
        )}

        <div className={styles.reportInfoBanner}>
          <InfoOutlinedIcon className={styles.reportInfoBannerIcon} />
          <span>
            Para contagem estimada, o relatório exige ações selecionadas e, conforme o modo, CPF válido ou municípios/regionais.
          </span>
        </div>

        <div className={styles.selectionSummaryCard}>
          <div className={styles.selectionSummaryTitle}>Resumo da seleção</div>
          <div className={styles.selectionSummaryGrid}>
            <div className={styles.selectionSummaryItem}>
              <CalendarMonthOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Período</div>
                <div className={styles.selectionSummaryValue}>
                  {formatDate(dataInicial)} a {formatDate(dataFinal)}
                </div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <GroupsOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Ações selecionadas</div>
                <div className={styles.selectionSummaryValue}>{selectedAcaoIds.length} ação(ões)</div>
              </div>
            </div>
            <div className={styles.selectionSummaryItem}>
              <PlaceOutlinedIcon className={styles.selectionSummaryIcon} />
              <div>
                <div className={styles.selectionSummaryLabel}>Modo de busca</div>
                <div className={styles.selectionSummaryValue}>
                  {searchMode === "cpf"
                    ? cpfSanitizado.length === 11
                      ? "CPF informado"
                      : "CPF pendente"
                    : todosMunicipiosEstado
                      ? "Todos municípios"
                      : `${selectedMunicipioIds.length} município(s) / ${selectedRegionaisIds.length} regional(is)`}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reportActionsRow}>
          <Button
            variant="outlined"
            className={stylesButton.buttonSoftMuted}
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleClear}
          >
            Limpar filtros
          </Button>

          <div className={styles.reportActionsRight}>
            <div className={styles.estimatedResultCard}>
              <div className={styles.estimatedResultLabel}>Resultado estimado</div>
              <div className={styles.estimatedResultValue}>
                {loadingCount ? <CircularProgress size={18} /> : estimatedCount ?? "-"}
              </div>
              <div className={styles.estimatedResultMeta}>registros</div>
            </div>

            <div className={styles.generateButtonGroup}>
              <Button
                variant="contained"
                className={styles.generateButton}
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleGenerateReport}
                disabled={generatingReport}
              >
                Gerar relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Backdrop
        open={generatingReport}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 100,
          flexDirection: "column",
          gap: 2,
          backgroundColor: "rgba(15, 23, 42, 0.45)",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ fontWeight: 600 }}>Gerando relatório...</Typography>
      </Backdrop>
    </>
  );
};

export default RelatorioConsultaParticipantes;
